<template>
  <div>
    <div ref="hierarchyField" class="hierarchy-field">
      <label class="hierarchy-field__label">{{ label }}</label>
      <button
        type="button"
        class="hierarchy-field__trigger"
        :class="{ 'hierarchy-field__trigger--error': showError }"
        @click="openHierarchyManager"
      >
        <span>
          {{ selectedHierarchyName || placeholder }}
        </span>
        <span class="hierarchy-field__caret">&#9662;</span>
      </button>
      <p v-if="showError" class="hierarchy-field__error">
        {{ errorText }}
      </p>
    </div>

    <div v-if="showHierarchyManager" class="hierarchy-manager mt-2" @click.stop>
      <div
        v-for="item in hierarchy"
        :key="item.id"
        class="hierarchy-manager__row"
        @click="selectHierarchy(item)"
      >
        <p class="hierarchy-manager__name">
          {{ getHierarchyName(item) }}
        </p>
        <button
          v-if="!item.isDefault"
          type="button"
          class="hierarchy-manager__action"
          :disabled="savingHierarchy"
          @click.stop="startEditHierarchy(item)"
        >
          Edit
        </button>
      </div>

      <button
        v-if="!showHierarchyInput"
        type="button"
        class="hierarchy-manager__add"
        :disabled="savingHierarchy"
        @click.stop="startAddHierarchy"
      >
        + Add Custom
      </button>

      <div v-else class="hierarchy-manager__editor">
        <input
          v-model.trim="hierarchyInputValue"
          type="text"
          class="hierarchy-manager__input"
          placeholder="--Enter--"
          :disabled="savingHierarchy"
        />
        <div class="hierarchy-manager__editor-actions">
          <button
            type="button"
            class="hierarchy-manager__save"
            :disabled="savingHierarchy || !hierarchyInputValue"
            @click="saveHierarchy"
          >
            Save
          </button>
          <button
            type="button"
            class="hierarchy-manager__cancel"
            :disabled="savingHierarchy"
            @click="cancelHierarchyInput"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HierarchySelect",
  props: {
    value: {
      type: [String, Number],
      default: null
    },
    label: {
      type: String,
      default: "Span of control"
    },
    placeholder: {
      type: String,
      default: "--Select--"
    },
    showError: {
      type: Boolean,
      default: false
    },
    errorText: {
      type: String,
      default: "Span of control is required."
    }
  },
  data() {
    return {
      hierarchy: [],
      showHierarchyManager: false,
      showHierarchyInput: false,
      editingHierarchyId: null,
      hierarchyInputValue: "",
      savingHierarchy: false
    };
  },
  computed: {
    selectedHierarchyName() {
      const selected = this.hierarchy.find(item => item.id === this.value);
      return selected ? this.getHierarchyName(selected) : "";
    }
  },
  methods: {
    async loadHierarchy() {
      const hierarchyResult = await this.$_getHierarchy();
      this.hierarchy = hierarchyResult.data.hierarchies || [];
    },
    getHierarchyName(item) {
      if (!item) return "";
      return item.name || item.label || item.title || "";
    },
    selectHierarchy(item) {
      this.$emit("input", item.id);
      this.$emit("clear-error");
      this.showHierarchyManager = false;
      this.cancelHierarchyInput();
    },
    openHierarchyManager() {
      this.showHierarchyManager = true;
      this.$emit("clear-error");
    },
    startAddHierarchy() {
      this.showHierarchyManager = true;
      this.showHierarchyInput = true;
      this.editingHierarchyId = null;
      this.hierarchyInputValue = "";
    },
    startEditHierarchy(item) {
      this.showHierarchyManager = true;
      this.showHierarchyInput = true;
      this.editingHierarchyId = item.id;
      this.hierarchyInputValue = this.getHierarchyName(item);
    },
    cancelHierarchyInput() {
      this.showHierarchyInput = false;
      this.editingHierarchyId = null;
      this.hierarchyInputValue = "";
    },
    handleClickOutsideHierarchy(event) {
      if (!this.showHierarchyManager || !this.$el) return;
      const clickedInsideComponent = this.$el.contains(event.target);
      if (!clickedInsideComponent) {
        this.showHierarchyManager = false;
        this.cancelHierarchyInput();
      }
    },
    async saveHierarchy() {
      const name = this.hierarchyInputValue.trim();
      if (!name) return;

      this.savingHierarchy = true;
      try {
        if (this.editingHierarchyId) {
          await this.$_editHierarchy(this.editingHierarchyId, { name });
          this.$toasted.success("Hierarchy updated successfully", {
            duration: 5000
          });
        } else {
          await this.$_addHierarchy({
            orgId: this.$orgId,
            name
          });
          this.$toasted.success("Hierarchy added successfully", {
            duration: 5000
          });
        }

        await this.loadHierarchy();

        const match = this.hierarchy.find(
          h => this.getHierarchyName(h).toLowerCase() === name.toLowerCase()
        );
        if (match && !this.editingHierarchyId) {
          this.$emit("input", match.id);
          this.$emit("clear-error");
        }

        this.cancelHierarchyInput();
      } catch (error) {
        this.$toasted.error("Unable to save hierarchy", {
          duration: 5000
        });
      } finally {
        this.savingHierarchy = false;
      }
    }
  },
  async mounted() {
    await this.loadHierarchy();
    document.addEventListener("click", this.handleClickOutsideHierarchy);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutsideHierarchy);
  }
};
</script>

<style scoped>
.hierarchy-field {
  width: 100%;
}

.hierarchy-field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #2f3941;
  font-weight: 600;
}

.hierarchy-field__trigger {
  width: 100%;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: black;
  font-size: 14px;
  text-align: left;
}

.hierarchy-field__trigger--error {
  border-color: #ef4444;
}

.hierarchy-field__caret {
  color: black;
  font-size: 18px;
}

.hierarchy-field__error {
  margin-top: 4px;
  font-size: 12px;
  color: #ef4444;
}

.hierarchy-manager {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fafafa;
  padding: 8px 10px 12px;
}

.hierarchy-manager__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.hierarchy-manager__row:last-of-type {
  border-bottom: none;
}

.hierarchy-manager__name {
    flex: 1;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.hierarchy-manager__name:hover {
    background: #e5e7eb;
}

.hierarchy-manager__action,
.hierarchy-manager__add,
.hierarchy-manager__save,
.hierarchy-manager__cancel {
  border: none;
  background: transparent;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.hierarchy-manager__action,
.hierarchy-manager__save {
  color: #f06431;
}

.hierarchy-manager__add {
  color: #f06431;
  margin-top: 8px;
  padding: 4px 0;
}

.hierarchy-manager__editor {
  margin-top: 8px;
}

.hierarchy-manager__input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 13px;
  background: #fff;
}

.hierarchy-manager__editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}

.hierarchy-manager__cancel {
  color: #6b7280;
}
</style>
